import type {
  ImageWorkerRequest,
  ImageWorkerResponse,
  ProcessedTshirtImage,
  TshirtGuide,
} from "../../../shared/image-processing";
import type { TshirtSide } from "../../../shared/tshirts";
import { createClientId } from "../identity/create-id";
import {
  createProcessImageRequest,
  createWarmupImageWorkerRequest,
} from "./message-contract";

export type ImageWorkerProcessor = {
  readonly prepare: (params?: {
    readonly onStatus?: (message: string) => void;
  }) => Promise<void>;
  readonly process: (params: {
    readonly file: File;
    readonly side: TshirtSide;
    readonly guide: TshirtGuide;
    readonly onStatus?: (message: string) => void;
    readonly debugSegmentation?: boolean;
    readonly primaryErrorMessage?: string;
  }) => Promise<ProcessedTshirtImage>;
  readonly dispose: () => void;
};

type ImageWorkerProcessorOptions = {
  readonly timeoutMs?: number;
  readonly createWorker?: () => Worker | null;
  readonly processInBrowser?: (
    params: Parameters<ImageWorkerProcessor["process"]>[0],
  ) => Promise<ProcessedTshirtImage>;
};

const defaultWorkerTimeoutMs = 120000;

const createRequestId = (): string => createClientId();

const createWorkerTimeoutError = (): Error =>
  new Error("画像処理がタイムアウトしました");

let defaultWorkerCreationErrorMessage: string | null = null;

const createDefaultWorker = (): Worker | null => {
  try {
    defaultWorkerCreationErrorMessage = null;

    return new Worker(new URL("./image-worker.ts", import.meta.url), {
      type: "module",
    });
  } catch (error: unknown) {
    defaultWorkerCreationErrorMessage =
      error instanceof Error ? error.message : "Unknown worker startup error";

    return null;
  }
};

const createWorkerUnavailableError = (): Error =>
  new Error(
    defaultWorkerCreationErrorMessage
      ? `画像処理Workerを起動できませんでした: ${defaultWorkerCreationErrorMessage}`
      : "画像処理Workerを起動できませんでした",
  );

const createBrowserFallbackMessage = (error: unknown): string =>
  error instanceof Error
    ? `画像処理Workerで失敗したためガイド型切り抜きに切り替えています: ${error.message}`
    : "画像処理Workerで失敗したためガイド型切り抜きに切り替えています";

const processWithDefaultBrowserFallback = (
  params: Parameters<ImageWorkerProcessor["process"]>[0],
): Promise<ProcessedTshirtImage> =>
  import("./browser-processor").then((module) =>
    module.processTshirtImageInBrowser(params),
  );

export const createImageWorkerProcessor = (
  worker: Worker | null = createDefaultWorker(),
  options: ImageWorkerProcessorOptions = {},
): ImageWorkerProcessor => {
  let activeWorker = worker;
  const recreateWorker = () => {
    activeWorker?.terminate();
    activeWorker = options.createWorker?.() ?? createDefaultWorker();
  };
  const getActiveWorker = (): Worker | null => {
    activeWorker =
      activeWorker ?? options.createWorker?.() ?? createDefaultWorker();

    return activeWorker;
  };
  const requestWithWorker = <Value>(params: {
    activeWorker: Worker;
    request: ImageWorkerRequest;
    onStatus?: (message: string) => void;
    resolveResponse: (response: ImageWorkerResponse) => Value | null;
  }) =>
    new Promise<Value>((resolve, reject) => {
      const timeoutMs = options.timeoutMs ?? defaultWorkerTimeoutMs;
      let timeoutHandle = window.setTimeout(() => {
        removeListeners();
        reject(createWorkerTimeoutError());
      }, timeoutMs);
      const refreshTimeout = () => {
        window.clearTimeout(timeoutHandle);
        timeoutHandle = window.setTimeout(() => {
          removeListeners();
          reject(createWorkerTimeoutError());
        }, timeoutMs);
      };
      const removeListeners = () => {
        window.clearTimeout(timeoutHandle);
        params.activeWorker.removeEventListener("message", handleMessage);
        params.activeWorker.removeEventListener("error", handleError);
        params.activeWorker.removeEventListener(
          "messageerror",
          handleMessageError,
        );
      };
      const handleMessage = (event: MessageEvent<ImageWorkerResponse>) =>
        event.data.id === params.request.id
          ? event.data.type === "process-image-status"
            ? (params.onStatus?.(event.data.message), refreshTimeout())
            : (() => {
                const value = params.resolveResponse(event.data);

                return value
                  ? (removeListeners(), resolve(value))
                  : event.data.type === "process-image-failure"
                    ? (removeListeners(), reject(new Error(event.data.message)))
                    : null;
              })()
          : null;
      const handleError = () => {
        removeListeners();
        reject(new Error("画像処理Workerを起動できませんでした"));
      };
      const handleMessageError = () => {
        removeListeners();
        reject(new Error("画像処理Workerとの通信に失敗しました"));
      };

      params.activeWorker.addEventListener("message", handleMessage);
      params.activeWorker.addEventListener("error", handleError);
      params.activeWorker.addEventListener("messageerror", handleMessageError);
      params.activeWorker.postMessage(params.request);
    });
  const rejectWithRecreatedWorker = (error: unknown): Promise<never> => {
    recreateWorker();

    return Promise.reject(error);
  };
  const processWithBrowserFallback = (
    params: Parameters<ImageWorkerProcessor["process"]>[0],
    error: unknown,
  ): Promise<ProcessedTshirtImage> => {
    recreateWorker();
    params.onStatus?.(createBrowserFallbackMessage(error));

    return (options.processInBrowser ?? processWithDefaultBrowserFallback)({
      ...params,
      primaryErrorMessage:
        error instanceof Error ? error.message : "画像処理Workerで失敗しました",
    });
  };
  const prepareWithWorker = (params?: {
    readonly onStatus?: (message: string) => void;
  }): Promise<void> => {
    const currentWorker = getActiveWorker();

    return currentWorker
      ? requestWithWorker({
          activeWorker: currentWorker,
          request: createWarmupImageWorkerRequest({
            id: createRequestId(),
          }),
          onStatus: params?.onStatus,
          resolveResponse: (response) =>
            response.type === "image-worker-ready" ? true : null,
        })
          .then(() => undefined)
          .catch(rejectWithRecreatedWorker)
      : Promise.reject(createWorkerUnavailableError());
  };
  const processWithWorker = (
    currentWorker: Worker,
    params: Parameters<ImageWorkerProcessor["process"]>[0],
  ): Promise<ProcessedTshirtImage> =>
    requestWithWorker({
      activeWorker: currentWorker,
      request: createProcessImageRequest({
        id: createRequestId(),
        file: params.file,
        side: params.side,
        guide: params.guide,
        debugSegmentation: params.debugSegmentation ?? false,
      }),
      onStatus: params.onStatus,
      resolveResponse: (response) =>
        response.type === "process-image-success" ? response.image : null,
    }).catch(rejectWithRecreatedWorker);

  return {
    prepare: prepareWithWorker,
    process: (params) => {
      const currentWorker = getActiveWorker();

      return currentWorker
        ? prepareWithWorker({ onStatus: params.onStatus }).then(() => {
            const preparedWorker = getActiveWorker();

            return preparedWorker
              ? processWithWorker(preparedWorker, params)
              : Promise.reject(createWorkerUnavailableError());
          }).catch((error: unknown) => processWithBrowserFallback(params, error))
        : processWithBrowserFallback(params, createWorkerUnavailableError());
    },
    dispose: () => {
      activeWorker?.terminate();
      activeWorker = null;
    },
  };
};
